import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/react';
import { Drawer } from '@/components/ui/Drawer';
import { Spinner } from '@/components/ui/Spinner';
import { StatusChip } from '@/components/common/StatusChip';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { PasswordInput } from '@/components/common/PasswordInput';
import { formatBytes } from '@/lib/format';
import { useCancelMigration, useMigration, useRerunMigration } from '@/features/migrations/hooks';
import { folderVerified, isActive, statusLabel, statusTone } from '@/features/migrations/status';
import { rerunMigrationSchema, type RerunMigrationValues } from '@/features/migrations/schema';
import { MigrationProgressBar } from '@/features/migrations/components/MigrationsTable';
import type { Migration } from '@/features/migrations/types';
import { ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon, NoSymbolIcon } from '@/icons';

const CUTOVER_STEPS = [
  'Check the verification table: every copied folder should show a matching destination count.',
  'Lower the TTL of the domain\'s MX record (e.g. 300 s) a day before switching.',
  'Point the domain\'s MX record at Nifty (Domains → DNS). The old provider keeps working until DNS propagates.',
  'After the MX change has propagated, run the migration again: it is incremental and copies only mail that arrived at the old provider in the meantime.',
  'Update mail apps and phones to Nifty\'s IMAP/SMTP settings, or set the account up again.',
  'Keep the old mailbox for at least 14 days before closing it.',
];

export function MigrationDetailDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { data: m, isLoading } = useMigration(id);
  const cancel = useCancelMigration();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [rerunOpen, setRerunOpen] = useState(false);

  const footer = m && (
    <div className="flex justify-end gap-2">
      {isActive(m) && !m.cancelRequested && (
        <Button color="danger" variant="flat" startContent={<NoSymbolIcon className="h-4 w-4" />} onPress={() => setConfirmCancel(true)}>Cancel migration</Button>
      )}
      {!isActive(m) && (
        <Button color="primary" startContent={<ArrowPathIcon className="h-4 w-4" />} onPress={() => setRerunOpen(true)}>Run again (incremental)</Button>
      )}
    </div>
  );

  return (
    <>
      <Drawer isOpen={!!id} onClose={onClose} title="Email migration" width="w-full max-w-2xl" footer={footer}>
        {isLoading || !m ? <div className="grid place-items-center py-16"><Spinner /></div> : <Detail m={m} />}
      </Drawer>
      {m && (
        <>
          <ConfirmDialog isOpen={confirmCancel} onClose={() => setConfirmCancel(false)}
            onConfirm={async () => { await cancel.mutateAsync(m.id); setConfirmCancel(false); }}
            title="Cancel this migration?" body="Copying stops after the current message. Mail already copied stays in the mailbox, and a later run continues where this one stopped."
            confirmLabel="Cancel migration" tone="danger" isLoading={cancel.isPending} />
          <RerunModal migration={m} isOpen={rerunOpen} onClose={() => setRerunOpen(false)} />
        </>
      )}
    </>
  );
}

function Detail({ m }: { m: Migration }) {
  const folders = m.folders ?? [];
  const mismatches = folders.filter((f) => folderVerified(f) === 'mismatch').length;
  const finished = m.status === 'completed' || m.status === 'completed_with_warnings';
  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <StatusChip tone={statusTone(m.status)}>{statusLabel(m)}</StatusChip>
          {m.attempt > 1 && <span className="text-xs text-fg-muted">attempt {m.attempt}</span>}
        </div>
        <dl className="grid grid-cols-[8rem_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-fg-muted">Into</dt><dd className="text-fg">{m.destinationAddress ?? 'Deleted mailbox'}</dd>
          <dt className="text-fg-muted">From</dt><dd className="text-fg">{m.source.username} @ {m.source.host}:{m.source.port} ({m.source.security === 'ssl' ? 'SSL/TLS' : m.source.security === 'starttls' ? 'STARTTLS' : 'plain'})</dd>
          {m.currentFolder && <><dt className="text-fg-muted">Copying</dt><dd className="text-fg">{m.currentFolder}</dd></>}
        </dl>
        <MigrationProgressBar m={m} />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Copied" value={m.progress.messagesCopied} />
          <Stat label="Already present" value={m.progress.messagesSkipped} />
          <Stat label="Not copied" value={m.progress.messagesFailed} tone={m.progress.messagesFailed > 0 ? 'text-danger' : undefined} />
          <Stat label="Data" value={formatBytes(m.progress.bytesCopied)} />
        </div>
      </section>

      {m.error && (
        <p role="alert" className="flex gap-2 rounded-lg bg-danger/10 p-3 text-sm text-danger">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
          <span><strong className="font-medium">{m.error.code.replace(/_/g, ' ').toLowerCase()}:</strong> {m.error.summary}</span>
        </p>
      )}

      <section>
        <h3 className="mb-2 text-sm font-semibold text-fg">Verification by folder</h3>
        {folders.length === 0 ? (
          <p className="text-sm text-fg-muted">Folders appear once the source account has been read.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-surface-raised text-left text-xs text-fg-muted">
                <tr><th className="px-3 py-2">Source folder</th><th className="px-3 py-2">Nifty folder</th><th className="px-3 py-2 text-right">Source</th><th className="px-3 py-2 text-right">Copied</th><th className="px-3 py-2 text-right">In Nifty</th><th className="px-3 py-2" /></tr>
              </thead>
              <tbody>
                {folders.map((f) => {
                  const v = folderVerified(f);
                  return (
                    <tr key={f.sourcePath} className="border-t border-border">
                      <td className="px-3 py-2 text-fg">{f.sourcePath}</td>
                      <td className="px-3 py-2 text-fg-muted">{f.status === 'skipped' ? '—' : f.destinationPath}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{f.status === 'skipped' ? '—' : f.messagesTotal}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{f.status === 'skipped' ? '—' : f.messagesCopied}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{f.destinationMessages ?? '—'}</td>
                      <td className="px-3 py-2">
                        {f.status === 'skipped' ? <span className="text-xs text-fg-subtle" title={f.errorSummary ?? ''}>skipped</span>
                          : v === 'ok' ? <CheckCircleIcon className="h-4 w-4 text-success" aria-label="Counts match" />
                          : v === 'mismatch' ? <ExclamationTriangleIcon className="h-4 w-4 text-warning" aria-label="Counts differ" />
                          : <span className="text-xs text-fg-subtle">{f.status}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {mismatches > 0 && <p className="mt-2 text-sm text-warning">{mismatches} folder(s) hold fewer messages in Nifty than at the source. Run the migration again, then check the warnings below.</p>}
      </section>

      {m.warnings.length > 0 && (
        <section>
          <h3 className="mb-2 text-sm font-semibold text-fg">Warnings ({m.warnings.length})</h3>
          <ul className="max-h-48 space-y-1 overflow-y-auto text-sm">
            {m.warnings.map((w, i) => (
              <li key={`${w.at}-${i}`} className="text-fg-muted"><span className="text-fg">{w.folder ?? 'Account'}:</span> {w.message}</li>
            ))}
          </ul>
        </section>
      )}

      {finished && (
        <section>
          <h3 className="mb-2 text-sm font-semibold text-fg">Cutover checklist</h3>
          <ol className="list-decimal space-y-1 pl-5 text-sm text-fg-muted">
            {CUTOVER_STEPS.map((s) => <li key={s}>{s}</li>)}
          </ol>
        </section>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number | string; tone?: string }) {
  return (
    <div className="rounded-lg bg-surface-raised p-3">
      <p className="text-xs text-fg-muted">{label}</p>
      <p className={`text-lg font-semibold tabular-nums ${tone ?? 'text-fg'}`}>{typeof value === 'number' ? value.toLocaleString() : value}</p>
    </div>
  );
}

function RerunModal({ migration, isOpen, onClose }: { migration: Migration; isOpen: boolean; onClose: () => void }) {
  const rerun = useRerunMigration();
  const form = useForm<RerunMigrationValues>({ resolver: zodResolver(rerunMigrationSchema), defaultValues: { sourcePassword: '' } });
  const submit = form.handleSubmit(async (v) => {
    await rerun.mutateAsync({ id: migration.id, sourcePassword: v.sourcePassword });
    form.reset();
    onClose();
  });
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalHeader className="text-fg">Run the migration again</ModalHeader>
        <ModalBody>
          <form id="rerun-form" onSubmit={submit} className="space-y-3" noValidate>
            <p className="text-sm text-fg-muted">
              Copies only mail that is not in {migration.destinationAddress ?? 'the mailbox'} yet. Enter the password for {migration.source.username} again — it was deleted when the last run ended.
            </p>
            <PasswordInput {...form.register('sourcePassword')} label="Password or app password" autoComplete="new-password"
              isInvalid={!!form.formState.errors.sourcePassword} errorMessage={form.formState.errors.sourcePassword?.message} />
          </form>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>Cancel</Button>
          <Button color="primary" type="submit" form="rerun-form" isLoading={form.formState.isSubmitting}>Start</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
